"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ColorBadges, colorShortCode } from "../components/ColorBadges";
import { CardDetailPanel } from "../components/CardDetailPanel";
import { getOpIndex, getSetSummaries } from "../lib/opIndex";
import { imageUrlForCardNumber } from "../lib/csvLogic";
import { parseQuery, searchLibrary } from "../lib/searchSyntax";
import type { IndexedCard, IndexedSetSummary, Deck, QueryNode } from "@punkrecords/types";
import {
  createEmptyDeck,
  serializeDecks,
  deserializeDecks,
  validateDeck,
  cardAllowsUnlimitedCopies,
  deckToText,
  deckToSim,
  textToDeck,
  deckToUrlParam,
  urlParamToDeck,
  getRulesForDeck,
  SEALED_ELIGIBLE_PREFIXES,
} from "@punkrecords/deck";

const PAGE_ID_PREFIX = "deckbuild";
const STORAGE_KEY = "punkrecords-decks-v1";

type ImageSource = "en" | "asia";

// ── localStorage helpers (thin wrappers around pure package functions) ──

function loadDecksFromStorage(): Deck[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return deserializeDecks(raw);
  } catch {
    return [];
  }
}

function saveDecksToStorage(decks: Deck[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, serializeDecks(decks));
  } catch {
    // ignore storage errors
  }
}

function LeaderDisplay({
  leader,
  lookupCard,
  resolveImageUrl,
  onDetail,
  onRemove,
}: {
  leader: string;
  lookupCard: (id: string) => IndexedCard | undefined;
  resolveImageUrl: (card: IndexedCard) => string;
  onDetail: () => void;
  onRemove: () => void;
}) {
  const card = lookupCard(leader);
  return (
    <div className="flex items-center gap-3">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={resolveImageUrl(
          card ?? ({ number: leader, imageUrl: "" } as IndexedCard),
        )}
        alt={leader}
        className="h-20 w-14 cursor-pointer rounded border border-slate-700 object-cover hover:border-blue-500"
        onClick={card ? onDetail : undefined}
      />
      <div
        className="min-w-0 flex-1 cursor-pointer"
        onClick={card ? onDetail : undefined}
      >
        <div className="text-sm font-medium text-slate-100">
          {card?.name ?? leader}
        </div>
        <div className="text-xs text-slate-400">{leader}</div>
        {card && <ColorBadges colors={card.colors} />}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 rounded-full border border-slate-700 px-2 py-0.5 text-2xs text-slate-400 hover:border-red-500 hover:text-red-400"
      >
        Remove
      </button>
    </div>
  );
}

// ── Page ──

export default function DeckbuildPage() {
  const index = useMemo(() => getOpIndex(), []);
  const cardByNumber = useMemo(() => {
    const map = new Map<string, IndexedCard>();
    for (const card of index.allCards) {
      if (!map.has(card.number)) map.set(card.number, card);
    }
    return map;
  }, [index]);

  const lookupCard = useCallback(
    (id: string) => cardByNumber.get(id.toUpperCase()),
    [cardByNumber],
  );

  // Deck state
  const [decks, setDecks] = useState<Deck[]>(() => loadDecksFromStorage());
  const [activeDeckId, setActiveDeckId] = useState<string | null>(null);
  const [showDeckList, setShowDeckList] = useState(true);

  const activeDeck = activeDeckId
    ? decks.find((d) => d.id === activeDeckId) ?? null
    : null;

  const rules = useMemo(
    () => (activeDeck ? getRulesForDeck(activeDeck) : null),
    [activeDeck],
  );

  const isSealed = activeDeck?.format === "sealed";

  const boosterSets = useMemo(() => {
    const summaries = getSetSummaries();
    return summaries.filter((s: IndexedSetSummary) =>
      SEALED_ELIGIBLE_PREFIXES.some((p) => s.code.startsWith(p)),
    );
  }, []);

  const [showSealedPicker, setShowSealedPicker] = useState(false);

  // Search state
  const [query, setQuery] = useState("");
  const [ast, setAst] = useState<QueryNode | null>(null);
  const [colorLocked, setColorLocked] = useState(true);

  // Import/export
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [imageSource, setImageSource] = useState<ImageSource>("en");

  // Card detail
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  const [detailCards, setDetailCards] = useState<IndexedCard[]>([]);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Persist decks
  useEffect(() => {
    saveDecksToStorage(decks);
  }, [decks]);

  // Load deck from URL ?d= param on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      const dParam = params.get("d");
      if (dParam) {
        const imported = urlParamToDeck(dParam);
        imported.name = imported.name || "Imported Deck";
        setDecks((prev) => [...prev, imported]);
        setActiveDeckId(imported.id);
        setShowDeckList(false);
      }
    } catch {
      // ignore bad URL params
    }
  }, []);

  const leaderColors = useMemo(() => {
    if (!activeDeck?.leader) return [];
    const card = lookupCard(activeDeck.leader);
    return card?.colors?.map((c: string) => c.toLowerCase()) ?? [];
  }, [activeDeck?.leader, lookupCard]);

  const effectiveQuery = useMemo(() => {
    const raw = query.trim();

    if (isSealed && activeDeck?.sealedSet && activeDeck.leader) {
      const isLeaderSearch = /^t:leader$/i.test(raw);
      if (isLeaderSearch) return raw;
      const setPrefix = `set:${activeDeck.sealedSet}`;
      if (!raw) return `${setPrefix} -t:leader`;
      return `${setPrefix} ${raw}`;
    }

    if (!colorLocked || leaderColors.length === 0) return raw;
    const colorPart = leaderColors.map((c: string) => `c:${c}`).join(" OR ");
    const prefix = leaderColors.length > 1 ? `(${colorPart})` : colorPart;
    const hasLeader = !!activeDeck?.leader;
    const leaderFilter = hasLeader ? " -t:leader" : "";
    if (!raw) return `${prefix}${leaderFilter}`;
    if (/\bc(olor)?:/i.test(raw)) return raw;
    return `${prefix} ${raw}${leaderFilter}`;
  }, [query, colorLocked, leaderColors, activeDeck?.leader, isSealed, activeDeck?.sealedSet]);

  // Parse query & sync URL
  useEffect(() => {
    if (!effectiveQuery) {
      setAst(null);
      return;
    }
    try {
      const parsed = parseQuery(effectiveQuery);
      setAst(parsed ?? null);
    } catch {
      setAst(null);
    }
  }, [effectiveQuery]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (effectiveQuery) {
      url.searchParams.set("q", effectiveQuery);
    } else {
      url.searchParams.delete("q");
    }
    window.history.replaceState({}, "", url.toString());
  }, [effectiveQuery]);

  const searchResults = useMemo(() => {
    if (!ast) return [];
    return searchLibrary(ast, index).slice(0, 200);
  }, [ast, index]);

  // ── Deck mutations ──

  function updateDeck(deckId: string, updater: (d: Deck) => Deck) {
    setDecks((prev) =>
      prev.map((d) => (d.id === deckId ? updater(d) : d)),
    );
  }

  function handleNewDeck() {
    const deck = createEmptyDeck();
    setDecks((prev) => [...prev, deck]);
    setActiveDeckId(deck.id);
    setShowDeckList(false);
    setQuery("t:leader");
  }

  function handleNewSealedDeck(setCode: string) {
    const deck: Deck = {
      ...createEmptyDeck(`Sealed — ${setCode}`),
      format: "sealed",
      sealedSet: setCode,
    };
    setDecks((prev) => [...prev, deck]);
    setActiveDeckId(deck.id);
    setShowDeckList(false);
    setShowSealedPicker(false);
    setQuery("t:leader");
  }

  function handleDeleteDeck(deckId: string) {
    setDecks((prev) => prev.filter((d) => d.id !== deckId));
    if (activeDeckId === deckId) {
      setActiveDeckId(null);
      setShowDeckList(true);
    }
    setDeleteConfirmId(null);
    setDeleteConfirmText("");
  }

  function handleSelectDeck(deckId: string) {
    setActiveDeckId(deckId);
    setShowDeckList(false);
  }

  function handleBackToList() {
    setActiveDeckId(null);
    setShowDeckList(true);
  }

  function handleSetLeader(cardId: string) {
    if (!activeDeckId) return;
    const card = lookupCard(cardId);
    updateDeck(activeDeckId, (d) => {
      const newDeck = { ...d, leader: cardId, updatedAt: Date.now() };
      if (card && d.name === "New Deck") {
        const colorPrefix = card.colors.map((c: string) => colorShortCode(c)).join("");
        newDeck.name = `${colorPrefix} ${card.name}`;
      }
      return newDeck;
    });
    if (query.trim().toLowerCase() === "t:leader") {
      setQuery("");
    }
  }

  function handleAddCard(cardId: string) {
    if (!activeDeckId || !activeDeck || !rules) return;
    const card = lookupCard(cardId);
    if (!card) return;

    if (card.class.toLowerCase() === "leader") {
      handleSetLeader(cardId);
      return;
    }

    const effectiveMaxCopies = rules.maxCopiesPerCard === -1
      ? rules.mainDeckSize
      : rules.maxCopiesPerCard;
    const maxForCard = cardAllowsUnlimitedCopies(card)
      ? rules.mainDeckSize
      : effectiveMaxCopies;
    const existing = activeDeck.cards.find((s) => s.cardId === cardId);
    if (existing && existing.count >= maxForCard) return;

    const currentTotal = activeDeck.cards.reduce((s, sl) => s + sl.count, 0);
    if (currentTotal >= rules.mainDeckSize) return;

    updateDeck(activeDeckId, (d) => {
      const slot = d.cards.find((s) => s.cardId === cardId);
      const newCards = slot
        ? d.cards.map((s) =>
            s.cardId === cardId ? { ...s, count: s.count + 1 } : s,
          )
        : [...d.cards, { cardId, count: 1 }];
      return { ...d, cards: newCards, updatedAt: Date.now() };
    });
  }

  function handleSearchResultRemove(cardId: string) {
    if (!activeDeckId || !activeDeck) return;
    if (activeDeck.leader === cardId) {
      handleRemoveLeader();
      return;
    }
    handleRemoveCard(cardId);
  }

  function handleRemoveCard(cardId: string) {
    if (!activeDeckId) return;
    updateDeck(activeDeckId, (d) => {
      const slot = d.cards.find((s) => s.cardId === cardId);
      if (!slot) return d;
      const newCards =
        slot.count <= 1
          ? d.cards.filter((s) => s.cardId !== cardId)
          : d.cards.map((s) =>
              s.cardId === cardId ? { ...s, count: s.count - 1 } : s,
            );
      return { ...d, cards: newCards, updatedAt: Date.now() };
    });
  }

  function handleRemoveAllCopies(cardId: string) {
    if (!activeDeckId) return;
    updateDeck(activeDeckId, (d) => ({
      ...d,
      cards: d.cards.filter((s) => s.cardId !== cardId),
      updatedAt: Date.now(),
    }));
  }

  function handleRemoveLeader() {
    if (!activeDeckId) return;
    updateDeck(activeDeckId, (d) => ({
      ...d,
      leader: null,
      updatedAt: Date.now(),
    }));
    setQuery("t:leader");
  }

  function handleDeckNameChange(name: string) {
    if (!activeDeckId) return;
    updateDeck(activeDeckId, (d) => ({
      ...d,
      name,
      updatedAt: Date.now(),
    }));
  }

  function copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise((resolve) => {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      resolve();
    });
  }

  function handleExportText() {
    if (!activeDeck) return;
    const text = deckToText(activeDeck, (id) => lookupCard(id)?.name);
    copyToClipboard(text).catch(() => {});
    const w = window.open("", "_blank");
    if (!w) return;
    const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;");
    w.document.write(`<pre style="font-family:monospace;font-size:13px;padding:16px;">${escaped}</pre>`);
    w.document.close();
  }

  function handleExportSim() {
    if (!activeDeck) return;
    const text = deckToSim(activeDeck);
    copyToClipboard(text).catch(() => {});
  }

  function handleExportUrl() {
    if (!activeDeck) return;
    const param = deckToUrlParam(activeDeck);
    const url = `${window.location.origin}/deckstats?d=${param}`;
    copyToClipboard(url).then(() => {
      setCopyFeedback("Copied!");
      setTimeout(() => setCopyFeedback(null), 2000);
    }).catch(() => {});
  }

  function handleClearDeck() {
    if (!activeDeckId) return;
    updateDeck(activeDeckId, (d) => ({
      ...d,
      cards: [],
      updatedAt: Date.now(),
    }));
  }

  function handleImportText() {
    if (!importText.trim()) return;
    const imported = textToDeck(importText);
    setDecks((prev) => [...prev, imported]);
    setActiveDeckId(imported.id);
    setShowDeckList(false);
    setShowImport(false);
    setImportText("");
  }

  // ── Validation ──

  const errors = useMemo(() => {
    if (!activeDeck) return [];
    return validateDeck(activeDeck, lookupCard);
  }, [activeDeck, lookupCard]);

  const totalCards = activeDeck
    ? activeDeck.cards.reduce((s, sl) => s + sl.count, 0)
    : 0;

  const deckCardsList = useMemo(() => {
    if (!activeDeck) return [];
    const leaderCard = activeDeck.leader ? lookupCard(activeDeck.leader) : null;
    const mainCards = activeDeck.cards
      .map((s) => lookupCard(s.cardId))
      .filter((c): c is IndexedCard => !!c);
    return leaderCard ? [leaderCard, ...mainCards] : mainCards;
  }, [activeDeck, lookupCard]);

  const resolveImageUrl = (card: IndexedCard) =>
    imageUrlForCardNumber(card.id, imageSource);

  /** Header logo / Deckbuild nav: back to deck list, clear search URL, close overlays. */
  const resetDeckbuildSession = useCallback(() => {
    setActiveDeckId(null);
    setShowDeckList(true);
    setQuery("");
    setShowImport(false);
    setImportText("");
    setDeleteConfirmId(null);
    setDeleteConfirmText("");
    setDetailOpen(false);
    setDetailIndex(null);
    setDetailCards([]);
    setCopyFeedback(null);
    setShowSealedPicker(false);
  }, []);

  // ── Render ──

  return (
    <div
      id={`${PAGE_ID_PREFIX}-page`}
      className="flex min-h-screen flex-col bg-slate-950 text-slate-50"
    >
      <Header
        idPrefix={`${PAGE_ID_PREFIX}-header`}
        deckbuildSessionReset={resetDeckbuildSession}
      />

      <main
        id={`${PAGE_ID_PREFIX}-main`}
        className="mx-auto flex w-full max-w-7xl flex-1 flex-col gap-4 px-4 py-5"
      >
        {/* ── Deck List View ── */}
        {showDeckList && !activeDeck && (
          <section className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-100">
                Your Decks
              </h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowImport(true)}
                  className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs text-slate-200 hover:border-slate-500"
                >
                  Import
                </button>
                <button
                  type="button"
                  onClick={() => setShowSealedPicker((v) => !v)}
                  className="rounded-full border border-amber-600 bg-amber-950/30 px-3 py-1 text-xs text-amber-300 hover:bg-amber-950/50"
                >
                  Sealed
                </button>
                <button
                  type="button"
                  onClick={handleNewDeck}
                  className="rounded-full border border-blue-600 bg-blue-950/40 px-3 py-1 text-xs text-blue-300 hover:bg-blue-950/60"
                >
                  + New Deck
                </button>
              </div>
            </div>

            {showImport && (
              <div className="mt-3 rounded-lg border border-slate-700 bg-slate-950/60 p-3">
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder={"Leader: OP01-001\n4 OP01-025\n3 OP01-051\n...\n\nSIM format:\n1xOP01-001\n4xOP01-025\n\nLimitless format:\nLeader\n1 Enel (OP15-058)\n\nCharacter (29)\n4 Charlotte Pudding (OP12-071)"}
                  rows={8}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 p-2 font-mono text-xs text-slate-100 outline-none focus:border-blue-500"
                />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={handleImportText}
                    className="rounded-full border border-blue-600 px-3 py-1 text-xs text-blue-300 hover:bg-blue-950/40"
                  >
                    Import Deck
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowImport(false);
                      setImportText("");
                    }}
                    className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-slate-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {showSealedPicker && (
              <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-950/10 p-3">
                <div className="mb-2 text-xs font-medium text-amber-300">
                  Choose a booster set for sealed play:
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {boosterSets.map((s: IndexedSetSummary) => (
                    <button
                      key={s.code}
                      type="button"
                      onClick={() => handleNewSealedDeck(s.code)}
                      className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-left text-xs text-slate-200 hover:border-amber-500 hover:bg-amber-950/20"
                    >
                      {s.image && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={s.image}
                          alt={s.code}
                          className="h-8 w-8 rounded object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-mono font-bold">{s.code}</div>
                        <div className="truncate text-2xs text-slate-400">{s.name}</div>
                      </div>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setShowSealedPicker(false)}
                  className="mt-2 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-slate-500"
                >
                  Cancel
                </button>
              </div>
            )}

            {decks.length === 0 && !showImport && !showSealedPicker && (
              <p className="mt-3 text-xs text-slate-400">
                No saved decks yet. Create one or import a deck list.
              </p>
            )}

            {decks.length > 0 && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {decks.map((d) => {
                  const leaderCard = d.leader ? lookupCard(d.leader) : null;
                  const count = d.cards.reduce((s, sl) => s + sl.count, 0);
                  return (
                    <div
                      key={d.id}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/60 p-3 hover:border-slate-600"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectDeck(d.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelectDeck(d.id);
                        }
                      }}
                    >
                      {leaderCard && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={resolveImageUrl(leaderCard)}
                          alt={leaderCard.name}
                          className="h-16 w-12 rounded border border-slate-700 object-cover hover:border-blue-500"
                        />
                      )}
                      {!leaderCard && (
                        <div className="flex h-16 w-12 items-center justify-center rounded border border-dashed border-slate-700 text-xs text-slate-500">
                          ?
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 truncate text-sm font-medium text-slate-100">
                          {d.name}
                          {d.format === "sealed" && (
                            <span className="shrink-0 rounded-full border border-amber-500/40 bg-amber-950/30 px-1.5 py-0.5 text-2xs font-medium text-amber-300">
                              Sealed{d.sealedSet ? ` · ${d.sealedSet}` : ""}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400">
                          {d.leader ?? "No leader"} · {count}/{getRulesForDeck(d).mainDeckSize} cards
                        </div>
                        <div className="text-2xs text-slate-500">
                          {new Date(d.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col gap-1">
                        {d.leader && count === getRulesForDeck(d).mainDeckSize && (
                          <a
                            href={`/deckstats?d=${deckToUrlParam(d)}`}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-full border border-purple-600 bg-purple-950/30 px-2 py-0.5 text-center text-2xs text-purple-300 hover:bg-purple-950/50"
                          >
                            Stats
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(d.id);
                            setDeleteConfirmText("");
                          }}
                          className="rounded-full border border-slate-700 px-2 py-0.5 text-2xs text-slate-400 hover:border-red-500 hover:text-red-400"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* ── Delete Confirmation Modal ── */}
        {deleteConfirmId && (() => {
          const deckToDelete = decks.find((d) => d.id === deleteConfirmId);
          if (!deckToDelete) return null;
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
              onClick={() => { setDeleteConfirmId(null); setDeleteConfirmText(""); }}
            >
              <div
                className="w-full max-w-sm rounded-xl border border-red-500/40 bg-slate-950 p-5"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-sm font-semibold text-red-400">Delete Deck</h3>
                <p className="mt-2 text-xs text-slate-300">
                  Type <span className="font-bold text-slate-100">{deckToDelete.name}</span> to confirm deletion.
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deckToDelete.name}
                  className="mt-2 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-red-500"
                  autoFocus
                />
                <div className="mt-3 flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => { setDeleteConfirmId(null); setDeleteConfirmText(""); }}
                    className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-slate-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={deleteConfirmText !== deckToDelete.name}
                    onClick={() => handleDeleteDeck(deleteConfirmId)}
                    className="rounded-full border border-red-600 px-3 py-1 text-xs text-red-300 hover:bg-red-950/40 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Delete Forever
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Deck Editor (two-panel) ── */}
        {activeDeck && (
          <>
            {activeDeck.leader && (
              <section className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 lg:hidden">
                <div className="mb-1 text-2xs font-medium uppercase tracking-wider text-slate-400">
                  Leader
                </div>
                <LeaderDisplay
                  leader={activeDeck.leader}
                  lookupCard={lookupCard}
                  resolveImageUrl={resolveImageUrl}
                  onDetail={() => { setDetailCards(deckCardsList); setDetailIndex(0); setDetailOpen(true); }}
                  onRemove={handleRemoveLeader}
                />
              </section>
            )}

            <div className="flex flex-col gap-4 lg:flex-row">
            {/* Left panel: Card search */}
            <section className="order-2 flex flex-1 flex-col gap-3 overflow-hidden rounded-xl border border-slate-800 bg-slate-900/70 p-4 lg:order-1">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleBackToList}
                  className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-slate-500"
                >
                  ← Decks
                </button>
                <span className="text-xs text-slate-400">
                  Left-click: add · Right-click: remove
                </span>
              </div>

              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  (e.target as HTMLFormElement).querySelector("input")?.blur();
                }}
              >
                <input
                  type="text"
                  enterKeyHint="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="set:OP01 t:character cost<=4"
                  className="min-w-0 flex-1 rounded-full border border-slate-700 bg-slate-950/80 px-4 py-2 text-sm text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50"
                />
                {leaderColors.length > 0 && !isSealed && (
                  <button
                    type="button"
                    onClick={() => setColorLocked((v) => !v)}
                    className={`flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1.5 text-2xs font-medium transition-colors ${
                      colorLocked
                        ? "border-blue-500 bg-blue-950/40 text-blue-300"
                        : "border-slate-700 text-slate-400 hover:border-slate-500"
                    }`}
                    title={colorLocked ? "Color filter locked to leader — click to unlock" : "Click to lock search to leader colors"}
                  >
                    <span>{colorLocked ? "🔒" : "🔓"}</span>
                    {leaderColors.map((c: string) => c.charAt(0).toUpperCase()).join("/")}
                  </button>
                )}
                <button
                  id={`${PAGE_ID_PREFIX}-image-source-en`}
                  type="button"
                  onClick={() => setImageSource("en")}
                  className={`shrink-0 rounded-full border px-2.5 py-1.5 text-2xs ${
                    imageSource === "en"
                      ? "border-emerald-500 text-emerald-300"
                      : "border-slate-700 text-slate-300 hover:border-slate-500"
                  }`}
                >
                  EN
                </button>
                <button
                  id={`${PAGE_ID_PREFIX}-image-source-asia`}
                  type="button"
                  onClick={() => setImageSource("asia")}
                  className={`shrink-0 rounded-full border px-2.5 py-1.5 text-2xs ${
                    imageSource === "asia"
                      ? "border-emerald-500 text-emerald-300"
                      : "border-slate-700 text-slate-300 hover:border-slate-500"
                  }`}
                >
                  EN (ASIA)
                </button>
              </form>

              {searchResults.length > 0 && (
                <div id="deckbuild-search-results" className="grid gap-2 overflow-y-auto sm:grid-cols-2 md:grid-cols-4">
                  {searchResults.map((card, searchIdx) => {
                    const inDeck =
                      activeDeck.cards.find((s) => s.cardId === card.number)
                        ?.count ?? 0;
                    const isLeader =
                      activeDeck.leader === card.number;
                    return (
                      <article
                        key={card.id}
                        className={`group flex cursor-pointer flex-col overflow-hidden rounded-lg border bg-slate-950/60 transition-colors hover:border-blue-500 ${
                          isLeader
                            ? "border-yellow-500/60"
                            : inDeck > 0
                              ? "border-blue-500/50"
                              : "border-slate-800"
                        }`}
                        tabIndex={0}
                        aria-label={`${card.name}. Left-click or Enter to add to deck. Right-click to remove one copy.`}
                        title="Left-click: add to deck · Right-click: remove from deck"
                        onClick={() => handleAddCard(card.number)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          handleSearchResultRemove(card.number);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleAddCard(card.number);
                          }
                        }}
                      >
                        <div className="relative w-full min-h-[80px] overflow-hidden" style={{ aspectRatio: "3/4" }}>
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={resolveImageUrl(card)}
                            alt={card.number}
                            loading="lazy"
                            className="pointer-events-none h-full w-full object-cover select-none"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetailCards(searchResults);
                              setDetailIndex(searchIdx);
                              setDetailOpen(true);
                            }}
                            onContextMenu={(e) => {
                              e.stopPropagation();
                            }}
                            className="deckbuild-detail-fab absolute left-1 top-1 z-20 flex h-6 w-6 translate-y-[20px] items-center justify-center rounded-full bg-slate-950/80 text-2xs text-slate-200 opacity-0 transition-opacity hover:bg-slate-800 group-hover:opacity-100"
                            title="View card details"
                          >
                            🔍
                          </button>
                          {(isLeader || inDeck > 0) && (
                            <div className="deckbuild-search-chip pointer-events-none absolute right-1 top-1 z-20 rounded-full bg-slate-950/80 px-2 py-0.5 text-2xs font-bold text-blue-300">
                              {isLeader ? "LDR" : `×${inDeck}`}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-0.5 px-2 py-1.5">
                          <div className="line-clamp-1 text-xs font-medium text-slate-100">
                            {card.name}
                          </div>
                          <div className="flex items-center justify-between text-2xs text-slate-400">
                            <span className="font-mono">
                              {card.setCode} {card.number}
                            </span>
                            <ColorBadges colors={card.colors} />
                            <span className="uppercase">{card.type}</span>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {effectiveQuery && searchResults.length === 0 && (
                <p className="text-xs text-slate-400">
                  No cards match. Try a different search.
                </p>
              )}

              {!effectiveQuery && (
                <p className="text-xs text-slate-400">
                  Type a search query to find cards. Examples:{" "}
                  <code className="rounded bg-slate-800 px-1">set:OP01</code>{" "}
                  <code className="rounded bg-slate-800 px-1">t:leader c:red</code>{" "}
                  <code className="rounded bg-slate-800 px-1">luffy</code>
                </p>
              )}
            </section>

            {/* Right panel: Deck contents */}
            <section className="order-1 flex flex-col gap-3 rounded-xl border border-slate-800 bg-slate-900/70 p-4 lg:order-2 lg:w-[40%]">
              <input
                type="text"
                value={activeDeck.name}
                onChange={(e) => handleDeckNameChange(e.target.value)}
                className="rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm font-semibold text-slate-100 outline-none focus:border-blue-500"
              />

              {/* Leader */}
              <div className={`rounded-lg border border-slate-800 bg-slate-950/60 p-3 ${activeDeck.leader ? "hidden lg:block" : ""}`}>
                <div className="mb-1 text-2xs font-medium uppercase tracking-wider text-slate-400">
                  Leader
                </div>
                {activeDeck.leader ? (
                  <LeaderDisplay
                    leader={activeDeck.leader}
                    lookupCard={lookupCard}
                    resolveImageUrl={resolveImageUrl}
                    onDetail={() => { setDetailCards(deckCardsList); setDetailIndex(0); setDetailOpen(true); }}
                    onRemove={handleRemoveLeader}
                  />
                ) : (
                  <p className="text-xs text-slate-500">
                    Search for a Leader card and click to set it.
                  </p>
                )}
              </div>

              {/* Card count */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-300">
                  Main Deck:{" "}
                  <span
                    className={
                      totalCards === (rules?.mainDeckSize ?? 50)
                        ? "font-bold text-emerald-400"
                        : "font-bold text-slate-100"
                    }
                  >
                    {totalCards}
                  </span>
                  /{rules?.mainDeckSize ?? 50}
                  {isSealed && (
                    <span className="ml-2 rounded-full border border-amber-500/40 bg-amber-950/30 px-1.5 py-0.5 text-2xs font-medium text-amber-300">
                      Sealed{activeDeck?.sealedSet ? ` · ${activeDeck.sealedSet}` : ""}
                    </span>
                  )}
                </span>
                {errors.length > 0 && (
                  <span className="deckbuild-validation-count text-red-400">
                    {errors.length} issue{errors.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {/* Validation errors */}
              {errors.length > 0 && (
                <div className="deckbuild-validation-panel max-h-24 overflow-y-auto rounded-md border border-red-500/30 bg-red-950/20 px-3 py-2">
                  {errors.map((err, i) => (
                    <div key={i} className="deckbuild-validation-line text-2xs text-red-300">
                      {err.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Card list */}
              <div id="deckbuild-deck-cards" className="flex flex-col gap-1">
                {activeDeck.cards.map((slot) => {
                  const card = lookupCard(slot.cardId);
                  return (
                    <div
                      key={slot.cardId}
                      className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-950/60 px-2 py-1.5"
                    >
                      {card && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={resolveImageUrl(card)}
                          alt={slot.cardId}
                          className="h-10 w-7 cursor-pointer rounded border border-slate-700 object-cover hover:border-blue-500"
                          onClick={() => {
                            const idx = deckCardsList.findIndex((c) => c.number === slot.cardId);
                            if (idx >= 0) { setDetailCards(deckCardsList); setDetailIndex(idx); setDetailOpen(true); }
                          }}
                        />
                      )}
                      <div
                        className="min-w-0 flex-1 cursor-pointer"
                        onClick={() => {
                          if (card) {
                            const idx = deckCardsList.findIndex((c) => c.number === slot.cardId);
                            if (idx >= 0) { setDetailCards(deckCardsList); setDetailIndex(idx); setDetailOpen(true); }
                          }
                        }}
                      >
                        <div className="truncate text-xs font-medium text-slate-100">
                          {card?.name ?? slot.cardId}
                        </div>
                        <div className="flex items-center gap-1 text-2xs text-slate-400">
                          <span className="font-mono">{slot.cardId}</span>
                          {card && <ColorBadges colors={card.colors} />}
                          {card && (
                            <span>
                              · {card.cost != null ? `C${card.cost}` : ""}{" "}
                              {card.power != null ? `P${card.power}` : ""}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleRemoveAllCopies(slot.cardId)}
                          className="flex h-6 w-6 items-center justify-center rounded border border-slate-700 text-2xs text-slate-400 hover:border-red-500 hover:text-red-400"
                          title="Remove all copies"
                        >
                          ✕
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveCard(slot.cardId)}
                          className="flex h-6 w-6 items-center justify-center rounded border border-slate-700 text-xs text-slate-300 hover:border-red-500 hover:text-red-400"
                        >
                          −
                        </button>
                        <span className="w-5 text-center text-xs font-bold text-slate-100">
                          {slot.count}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleAddCard(slot.cardId)}
                          className="flex h-6 w-6 items-center justify-center rounded border border-slate-700 text-xs text-slate-300 hover:border-blue-500 hover:text-blue-400"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
                {activeDeck.cards.length === 0 && (
                  <p className="py-4 text-center text-xs text-slate-500">
                    No cards yet. Search and left-click a card to add.
                  </p>
                )}
              </div>

              {/* Export / import / actions */}
              <div className="flex flex-wrap gap-2 border-t border-slate-800 pt-3">
                <button
                  type="button"
                  onClick={handleExportSim}
                  className="rounded-full border border-emerald-600 bg-emerald-950/30 px-3 py-1 text-xs text-emerald-300 hover:bg-emerald-950/50"
                >
                  Copy for SIM
                </button>
                <button
                  type="button"
                  onClick={handleExportText}
                  className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-slate-500"
                >
                  Export Text
                </button>
                <button
                  type="button"
                  onClick={handleExportUrl}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    copyFeedback
                      ? "border-emerald-500 text-emerald-300"
                      : "border-slate-700 text-slate-300 hover:border-slate-500"
                  }`}
                >
                  {copyFeedback ?? "Share URL"}
                </button>
                <a
                  href={`/deckstats?d=${deckToUrlParam(activeDeck)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-purple-600 bg-purple-950/30 px-3 py-1 text-xs text-purple-300 hover:bg-purple-950/50"
                >
                  View Stats
                </a>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Remove all cards from this deck?")) {
                      handleClearDeck();
                    }
                  }}
                  className="rounded-full border border-red-700/50 px-3 py-1 text-xs text-red-400 hover:border-red-500 hover:bg-red-950/30"
                >
                  Remove All
                </button>
              </div>
            </section>
            </div>
          </>
        )}
      </main>

      <Footer idPrefix={`${PAGE_ID_PREFIX}-footer`} />

      <CardDetailPanel
        idPrefix={`${PAGE_ID_PREFIX}-detail`}
        isOpen={detailOpen}
        cards={detailCards}
        currentIndex={detailIndex}
        onClose={() => setDetailOpen(false)}
        onNavigate={setDetailIndex}
        resolveImageUrl={resolveImageUrl}
      />
    </div>
  );
}
